import { DropdownMenu } from '@creator/ui/components/DropdownMenu/DropdownMenu';
import { DropdownMenuContent } from '@creator/ui/components/DropdownMenu/DropdownMenuContent';
import { DropdownMenuItem } from '@creator/ui/components/DropdownMenu/DropdownMenuItem';
import { DropdownMenuTrigger } from '@creator/ui/components/DropdownMenu/DropdownMenuTrigger';
import Th from '@creator/ui/components/Table/Th/Th';
import Tr from '@creator/ui/components/Table/Tr/Tr';
import Text from '@creator/ui/components/Text/Text';
import Icon from '@src/basic-components/Icon/Icon';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ReportTableHeaderProps {
    selectedStatus: string;
    selectedType: string;
    onStatusChange: (status: string) => void;
    onTypeChange: (type: string) => void;
}

const ReportTableHeader: FC<ReportTableHeaderProps> = ({ selectedStatus, selectedType, onStatusChange, onTypeChange }) => {
    const { t } = useTranslation();

    const STATUS_OPTIONS = ['all', 'new', 'done', 'rejected'];
    const TYPE_OPTIONS = ['all', 'user', 'post', 'comment', 'community'];

    function renderTableHeaderCell(type: string, className = '') {
        return (
            <Th className={className}>
                <Text className="font-semibold">{t(`tokenPage.reports.columns.${type}`)}</Text>
            </Th>
        );
    }

    function renderTableHeaderStatus() {
        const statusKey = selectedStatus === 'all' ? 'status' : selectedStatus.toLowerCase();
        return (
            <Th className="">
                <DropdownMenu>
                    <DropdownMenuTrigger>
                        <div className="flex items-center justify-center cursor-pointer">
                            <Text className="mr-2 font-semibold">{t(`tokenPage.reports.columns.${statusKey}`)}</Text>
                            <Icon name="caretDown" />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-32">
                        {STATUS_OPTIONS.map(status => (
                            <DropdownMenuItem
                                key={status}
                                onClick={() => onStatusChange(status)}
                                className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 ${selectedStatus === status ? 'bg-primary-100 dark:bg-primary-900 font-bold' : ''}`}
                            >
                                <Text>{t(`tokenPage.reports.columns.${status}`)}</Text>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </Th>
        );
    }

    function renderTableHeaderTypeCell() {
        const typeKey = selectedType === 'all' ? 'type' : selectedType.toLowerCase();
        return (
            <Th className="">
                <DropdownMenu>
                    <DropdownMenuTrigger>
                        <div className="flex items-center justify-center cursor-pointer">
                            <Text className="mr-2 font-semibold">{t(`tokenPage.reports.columns.${typeKey}`)}</Text>
                            <Icon name="caretDown" />
                        </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="min-w-32">
                        {TYPE_OPTIONS.map(type => (
                            <DropdownMenuItem
                                key={type}
                                onClick={() => onTypeChange(type)}
                                className={`px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 ${selectedType === type ? 'bg-primary-100 dark:bg-primary-900 font-bold' : ''}`}
                            >
                                <Text>{t(`tokenPage.reports.columns.${type}`)}</Text>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
            </Th>
        );
    }

    return (
        <Tr>
            {renderTableHeaderCell('time')}
            {renderTableHeaderStatus()}
            {renderTableHeaderTypeCell()}
            {renderTableHeaderCell('reporter')}
            {renderTableHeaderCell('reported')}
            {renderTableHeaderCell('postComment')}
            {renderTableHeaderCell('reason')}
        </Tr>
    );
};

export default ReportTableHeader;
